// @ts-nocheck
// Reviews list + detail modal. Pulls reviews joined with the invitation
// row so we can show the reviewer's name; rating is rendered as 1-5 dots,
// other answers as paragraphs in the detail modal.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { REVIEW_QUESTIONS } from "../../content/he/reviewer";
import { useTableSort, SortableTH } from "./useTableSort";

type ReviewRow = {
  id: string;
  user_id: string;
  responses: Record<string, any>;
  submitted_at: string;
  invitation: { name: string; email: string } | null;
};

export function AdminReviews() {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState<ReviewRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Reviews may or may not have a matching invitation; we left-join
      // by querying invitations separately and stitching client-side
      // (Supabase REST joins are easiest with a foreign-key relation,
      // which we don't have between reviews.user_id and invitations).
      const reviews = await supabase
        .from("reviews")
        .select("id, user_id, responses, submitted_at")
        .order("submitted_at", { ascending: false });
      if (cancelled) return;
      if (reviews.error) {
        setError(reviews.error.message);
        setLoading(false);
        return;
      }
      const userIds = (reviews.data || []).map((r) => r.user_id);
      let invitationsByUser: Record<string, { name: string; email: string }> = {};
      if (userIds.length) {
        const inv = await supabase
          .from("reviewer_invitations")
          .select("name, email, claimed_by_user_id")
          .in("claimed_by_user_id", userIds);
        if (cancelled) return;
        for (const r of inv.data || []) {
          if (r.claimed_by_user_id) {
            invitationsByUser[r.claimed_by_user_id] = {
              name: r.name,
              email: r.email,
            };
          }
        }
      }
      setRows(
        (reviews.data || []).map((r) => ({
          ...r,
          invitation: invitationsByUser[r.user_id] || null,
        }))
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { sortBy, sortIndicator, sortedRows } = useTableSort(rows, {
    reader: (r) => r.invitation?.name || "",
    rating: (r) => (typeof r.responses?.overall_rating === "number" ? r.responses.overall_rating : null),
    submitted_at: (r) => r.submitted_at,
  });

  return (
    <>
      {error && <div className="adm-error">{error}</div>}

      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-loading">טוען משובים…</div>
        ) : rows.length === 0 ? (
          <div className="adm-empty">
            עדיין לא הוגשו משובים. שלחו הזמנות מהלשונית הקודמת.
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <SortableTH k="reader" sortBy={sortBy} sortIndicator={sortIndicator}>קורא/ת</SortableTH>
                <SortableTH k="rating" sortBy={sortBy} sortIndicator={sortIndicator}>דירוג</SortableTH>
                <SortableTH k="submitted_at" sortBy={sortBy} sortIndicator={sortIndicator}>תאריך</SortableTH>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => {
                const rating = r.responses?.overall_rating;
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {r.invitation?.name || "(משתמש ללא הזמנה)"}
                      </div>
                      <div className="mono" style={{ color: "#8B7355" }}>
                        {r.invitation?.email || ""}
                      </div>
                    </td>
                    <td className="num">
                      {typeof rating === "number" ? `${rating} / 5` : "—"}
                    </td>
                    <td className="num">
                      {new Date(r.submitted_at).toLocaleDateString("he-IL")}
                    </td>
                    <td>
                      <button
                        className="adm-btn secondary tiny"
                        onClick={() => setActive(r)}
                      >
                        פירוט
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {active && <ReviewDetail row={active} onClose={() => setActive(null)} />}
    </>
  );
}

function ReviewDetail({
  row,
  onClose,
}: {
  row: ReviewRow;
  onClose: () => void;
}) {
  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose}>
          ×
        </button>
        <h3 style={{ fontFamily: "'Frank Ruhl Libre', serif", fontSize: 22, color: "#5C3D2E" }}>
          {row.invitation?.name || "משוב"}
        </h3>
        <div style={{ color: "#8B7355", fontSize: 13, marginBottom: 22 }}>
          {row.invitation?.email} · {new Date(row.submitted_at).toLocaleString("he-IL")}
        </div>

        {REVIEW_QUESTIONS.map((q) => {
          const ans = row.responses?.[q.id];
          if (ans === undefined || ans === null || ans === "") return null;
          return (
            <div key={q.id} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#5C3D2E", marginBottom: 4 }}>
                {q.prompt}
              </div>
              <div
                style={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  color: "#2D1B10",
                  whiteSpace: "pre-wrap",
                  background: "#FAF7F2",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1px solid #E8DCC8",
                }}
              >
                {q.kind === "rating" ? `${ans} / 5` : ans}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
